"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.packRevealDepositParameters = exports.EthereumBridge = void 0;
const contracts_1 = require("../contracts");
const ethers_1 = require("ethers");
const utils_1 = require("../utils");
const bitcoin_1 = require("../bitcoin");
const adapter_1 = require("./adapter");
const address_1 = require("./address");
const wallet_registry_1 = require("./wallet-registry");
const Bridge_json_1 = __importDefault(require("./artifacts/mainnet/Bridge.json"));
const Bridge_json_2 = __importDefault(require("./artifacts/sepolia/Bridge.json"));
const Bridge_json_3 = __importDefault(require("@keep-network/tbtc-v2/artifacts/Bridge.json"));
/**
 * Implementation of the Ethereum Bridge handle.
 * @see {Bridge} for reference.
 */
class EthereumBridge extends adapter_1.EthersContractHandle {
    constructor(config, chainId = contracts_1.Chains.Ethereum.Local) {
        let deployment;
        switch (chainId) {
            case contracts_1.Chains.Ethereum.Local:
                deployment = Bridge_json_3.default;
                break;
            case contracts_1.Chains.Ethereum.Sepolia:
                deployment = Bridge_json_2.default;
                break;
            case contracts_1.Chains.Ethereum.Mainnet:
                deployment = Bridge_json_1.default;
                break;
            default:
                throw new Error("Unsupported deployment type");
        }
        super(config, deployment);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {Bridge#getChainIdentifier}
     */
    getChainIdentifier() {
        return address_1.EthereumAddress.from(this._instance.address);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {Bridge#getDepositRevealedEvents}
     */
    async getDepositRevealedEvents(options, ...filterArgs) {
        const events = await this.getEvents("DepositRevealed", options, ...filterArgs);
        return events.map((event) => {
            return {
                blockNumber: ethers_1.BigNumber.from(event.blockNumber).toNumber(),
                blockHash: utils_1.Hex.from(event.blockHash),
                transactionHash: utils_1.Hex.from(event.transactionHash),
                fundingTxHash: bitcoin_1.BitcoinTxHash.from(event.args.fundingTxHash).reverse(),
                fundingOutputIndex: ethers_1.BigNumber.from(event.args.fundingOutputIndex).toNumber(),
                depositor: address_1.EthereumAddress.from(event.args.depositor),
                amount: ethers_1.BigNumber.from(event.args.amount),
                blindingFactor: utils_1.Hex.from(event.args.blindingFactor),
                walletPublicKeyHash: utils_1.Hex.from(event.args.walletPubKeyHash),
                refundPublicKeyHash: utils_1.Hex.from(event.args.refundPubKeyHash),
                refundLocktime: utils_1.Hex.from(event.args.refundLocktime),
                vault: event.args.vault === ethers_1.constants.AddressZero
                    ? undefined
                    : address_1.EthereumAddress.from(event.args.vault),
            };
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {Bridge#pendingRedemptions}
     */
    async pendingRedemptions(walletPublicKey, redeemerOutputScript) {
        const walletPublicKeyHash = bitcoin_1.BitcoinHashUtils.computeHash160(walletPublicKey);
        return this.pendingRedemptionsByWalletPKH(walletPublicKeyHash, redeemerOutputScript);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {Bridge#pendingRedemptionsByWalletPKH}
     */
    async pendingRedemptionsByWalletPKH(walletPublicKeyHash, redeemerOutputScript) {
        const redemptionKey = EthereumBridge.buildRedemptionKey(walletPublicKeyHash, redeemerOutputScript);
        const request = await (0, utils_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.pendingRedemptions(redemptionKey);
        });
        return this.parseRedemptionRequest(request, redeemerOutputScript);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {Bridge#timedOutRedemptions}
     */
    async timedOutRedemptions(walletPublicKey, redeemerOutputScript) {
        const redemptionKey = EthereumBridge.buildRedemptionKey(bitcoin_1.BitcoinHashUtils.computeHash160(walletPublicKey), redeemerOutputScript);
        const request = await (0, utils_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.timedOutRedemptions(redemptionKey);
        });
        return this.parseRedemptionRequest(request, redeemerOutputScript);
    }
    /**
     * Builds a redemption key required to refer a redemption request.
     * @param walletPublicKeyHash The wallet public key hash that identifies the
     *        pending redemption (along with the redeemer output script).
     * @param redeemerOutputScript The redeemer output script that identifies the
     *        pending redemption (along with the wallet public key hash). Must not
     *        be prepended with length.
     * @returns The redemption key.
     */
    static buildRedemptionKey(walletPublicKeyHash, redeemerOutputScript) {
        // Convert the output script to raw bytes buffer.
        const rawRedeemerOutputScript = redeemerOutputScript.toBuffer();
        // Prefix the output script bytes buffer with 0x and its own length.
        const prefixedRawRedeemerOutputScript = `0x${Buffer.concat([
            Buffer.from([rawRedeemerOutputScript.length]),
            rawRedeemerOutputScript,
        ]).toString("hex")}`;
        // Build the redemption key by using the 0x-prefixed wallet PKH and
        // prefixed output script.
        return ethers_1.utils.solidityKeccak256(["bytes32", "bytes20"], [
            ethers_1.utils.solidityKeccak256(["bytes"], [prefixedRawRedeemerOutputScript]),
            `0x${walletPublicKeyHash.toString()}`,
        ]);
    }
    /**
     * Parses a redemption request using data fetched from the on-chain contract.
     * @param request Data of the request.
     * @param redeemerOutputScript The redeemer output script that identifies the
     *        pending redemption (along with the wallet public key hash). Must not
     *        be prepended with length.
     * @returns Parsed redemption request.
     */
    parseRedemptionRequest(request, redeemerOutputScript) {
        return {
            redeemer: address_1.EthereumAddress.from(request.redeemer),
            redeemerOutputScript: redeemerOutputScript,
            requestedAmount: ethers_1.BigNumber.from(request.requestedAmount),
            treasuryFee: ethers_1.BigNumber.from(request.treasuryFee),
            txMaxFee: ethers_1.BigNumber.from(request.txMaxFee),
            requestedAt: ethers_1.BigNumber.from(request.requestedAt).toNumber(),
        };
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {Bridge#revealDeposit}
     */
    async revealDeposit(depositTx, depositOutputIndex, deposit, vault) {
        const { fundingTx, reveal, extraData } = packRevealDepositParameters(depositTx, depositOutputIndex, deposit, vault);
        const tx = await adapter_1.EthersTransactionUtils.sendWithRetry(async () => {
            if (typeof extraData !== "undefined") {
                return await this._instance.revealDepositWithExtraData(fundingTx, reveal, extraData);
            }
            return await this._instance.revealDeposit(fundingTx, reveal);
        }, this._totalRetryAttempts, undefined, ["Deposit already revealed"]);
        return utils_1.Hex.from(tx.hash);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {Bridge#submitDepositSweepProof}
     */
    async submitDepositSweepProof(sweepTx, sweepProof, mainUtxo, vault) {
        const sweepTxParam = {
            version: `0x${sweepTx.version}`,
            inputVector: `0x${sweepTx.inputs}`,
            outputVector: `0x${sweepTx.outputs}`,
            locktime: `0x${sweepTx.locktime}`,
        };
        const sweepProofParam = {
            merkleProof: sweepProof.merkleProof.toPrefixedString(),
            txIndexInBlock: sweepProof.txIndexInBlock,
            bitcoinHeaders: sweepProof.bitcoinHeaders.toPrefixedString(),
            coinbasePreimage: sweepProof.coinbasePreimage.toPrefixedString(),
            coinbaseProof: sweepProof.coinbaseProof.toPrefixedString(),
        };
        const mainUtxoParam = {
            // The Ethereum Bridge expects this hash to be in the Bitcoin internal
            // byte order.
            txHash: mainUtxo.transactionHash.reverse().toPrefixedString(),
            txOutputIndex: mainUtxo.outputIndex,
            txOutputValue: mainUtxo.value,
        };
        const vaultParam = vault
            ? `0x${vault.identifierHex}`
            : ethers_1.constants.AddressZero;
        const tx = await adapter_1.EthersTransactionUtils.sendWithRetry(async () => {
            return await this._instance.submitDepositSweepProof(sweepTxParam, sweepProofParam, mainUtxoParam, vaultParam);
        }, this._totalRetryAttempts);
        return utils_1.Hex.from(tx.hash);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {Bridge#txProofDifficultyFactor}
     */
    async txProofDifficultyFactor() {
        const txProofDifficultyFactor = await (0, utils_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.txProofDifficultyFactor();
        });
        return txProofDifficultyFactor.toNumber();
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {Bridge#requestRedemption}
     */
    async requestRedemption(walletPublicKey, mainUtxo, redeemerOutputScript, amount) {
        const walletPublicKeyHash = bitcoin_1.BitcoinHashUtils.computeHash160(walletPublicKey).toPrefixedString();
        const mainUtxoParam = {
            // The Ethereum Bridge expects this hash to be in the Bitcoin internal
            // byte order.
            txHash: mainUtxo.transactionHash.reverse().toPrefixedString(),
            txOutputIndex: mainUtxo.outputIndex,
            txOutputValue: mainUtxo.value,
        };
        // Convert the output script to raw bytes buffer.
        const rawRedeemerOutputScript = redeemerOutputScript.toBuffer();
        // Prefix the output script bytes buffer with 0x and its own length.
        const prefixedRawRedeemerOutputScript = `0x${Buffer.concat([
            Buffer.from([rawRedeemerOutputScript.length]),
            rawRedeemerOutputScript,
        ]).toString("hex")}`;
        const tx = await adapter_1.EthersTransactionUtils.sendWithRetry(async () => {
            return await this._instance.requestRedemption(walletPublicKeyHash, mainUtxoParam, prefixedRawRedeemerOutputScript, amount);
        }, this._totalRetryAttempts);
        return utils_1.Hex.from(tx.hash);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {Bridge#submitRedemptionProof}
     */
    async submitRedemptionProof(redemptionTx, redemptionProof, mainUtxo, walletPublicKey) {
        const redemptionTxParam = {
            version: `0x${redemptionTx.version}`,
            inputVector: `0x${redemptionTx.inputs}`,
            outputVector: `0x${redemptionTx.outputs}`,
            locktime: `0x${redemptionTx.locktime}`,
        };
        const redemptionProofParam = {
            merkleProof: redemptionProof.merkleProof.toPrefixedString(),
            txIndexInBlock: redemptionProof.txIndexInBlock,
            bitcoinHeaders: redemptionProof.bitcoinHeaders.toPrefixedString(),
            coinbasePreimage: redemptionProof.coinbasePreimage.toPrefixedString(),
            coinbaseProof: redemptionProof.coinbaseProof.toPrefixedString(),
        };
        const mainUtxoParam = {
            // The Ethereum Bridge expects this hash to be in the Bitcoin internal
            // byte order.
            txHash: mainUtxo.transactionHash.reverse().toPrefixedString(),
            txOutputIndex: mainUtxo.outputIndex,
            txOutputValue: mainUtxo.value,
        };
        const walletPublicKeyHash = bitcoin_1.BitcoinHashUtils.computeHash160(walletPublicKey).toPrefixedString();
        const tx = await adapter_1.EthersTransactionUtils.sendWithRetry(async () => {
            return await this._instance.submitRedemptionProof(redemptionTxParam, redemptionProofParam, mainUtxoParam, walletPublicKeyHash);
        }, this._totalRetryAttempts);
        return utils_1.Hex.from(tx.hash);
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {Bridge#deposits}
     */
    async deposits(depositTxHash, depositOutputIndex) {
        const depositKey = EthereumBridge.buildDepositKey(depositTxHash, depositOutputIndex);
        const deposit = await (0, utils_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.deposits(depositKey);
        });
        return this.parseDepositRequest(deposit);
    }
    /**
     * Builds the deposit key required to refer a revealed deposit.
     * @param depositTxHash The revealed deposit transaction's hash.
     * @param depositOutputIndex Index of the deposit transaction output that
     *        funds the revealed deposit.
     * @returns Deposit key.
     */
    static buildDepositKey(depositTxHash, depositOutputIndex) {
        const prefixedReversedDepositTxHash = depositTxHash
            .reverse()
            .toPrefixedString();
        return ethers_1.utils.solidityKeccak256(["bytes32", "uint32"], [prefixedReversedDepositTxHash, depositOutputIndex]);
    }
    /**
     * Parses a deposit request using data fetched from the on-chain contract.
     * @param deposit Data of the deposit request.
     * @returns Parsed deposit request.
     */
    parseDepositRequest(deposit) {
        return {
            depositor: address_1.EthereumAddress.from(deposit.depositor),
            amount: ethers_1.BigNumber.from(deposit.amount),
            vault: deposit.vault === ethers_1.constants.AddressZero
                ? undefined
                : address_1.EthereumAddress.from(deposit.vault),
            revealedAt: ethers_1.BigNumber.from(deposit.revealedAt).toNumber(),
            sweptAt: ethers_1.BigNumber.from(deposit.sweptAt).toNumber(),
            treasuryFee: ethers_1.BigNumber.from(deposit.treasuryFee),
        };
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {Bridge#activeWalletPublicKey}
     */
    async activeWalletPublicKey() {
        const activeWalletPublicKeyHash = await (0, utils_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.activeWalletPubKeyHash();
        });
        if (activeWalletPublicKeyHash === "0x0000000000000000000000000000000000000000") {
            // If there is no active wallet currently, return undefined.
            return undefined;
        }
        const { walletPublicKey } = await this.wallets(utils_1.Hex.from(activeWalletPublicKeyHash));
        return walletPublicKey;
    }
    async getWalletCompressedPublicKey(ecdsaWalletID) {
        const walletRegistry = await this.walletRegistry();
        try {
            const uncompressedPublicKey = await walletRegistry.getWalletPublicKey(ecdsaWalletID);
            return utils_1.Hex.from(bitcoin_1.BitcoinPublicKeyUtils.compressPublicKey(uncompressedPublicKey));
        }
        catch (error) {
            console.log(`cannot get wallet public key for ${ecdsaWalletID}; error: ${error}`);
            return undefined;
        }
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {Bridge#getNewWalletRegisteredEvents}
     */
    async getNewWalletRegisteredEvents(options, ...filterArgs) {
        const events = await this.getEvents("NewWalletRegistered", options, ...filterArgs);
        return events.map((event) => {
            return {
                blockNumber: ethers_1.BigNumber.from(event.blockNumber).toNumber(),
                blockHash: utils_1.Hex.from(event.blockHash),
                transactionHash: utils_1.Hex.from(event.transactionHash),
                ecdsaWalletID: utils_1.Hex.from(event.args.ecdsaWalletID),
                walletPublicKeyHash: utils_1.Hex.from(event.args.walletPubKeyHash),
            };
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {Bridge#walletRegistry}
     */
    async walletRegistry() {
        const { ecdsaWalletRegistry } = await (0, utils_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.contractReferences();
        });
        return new wallet_registry_1.EthereumWalletRegistry({
            address: ecdsaWalletRegistry,
            signerOrProvider: this._instance.signer || this._instance.provider,
        });
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {Bridge#wallets}
     */
    async wallets(walletPublicKeyHash) {
        const wallet = await (0, utils_1.backoffRetrier)(this._totalRetryAttempts)(async () => {
            return await this._instance.wallets(walletPublicKeyHash.toPrefixedString());
        });
        return this.parseWalletDetails(wallet);
    }
    /**
     * Parses a wallet data using data fetched from the on-chain contract.
     * @param wallet Data of the wallet.
     * @returns Parsed wallet data.
     */
    async parseWalletDetails(wallet) {
        const ecdsaWalletID = utils_1.Hex.from(wallet.ecdsaWalletID);
        return {
            ecdsaWalletID,
            walletPublicKey: await this.getWalletCompressedPublicKey(ecdsaWalletID),
            mainUtxoHash: utils_1.Hex.from(wallet.mainUtxoHash),
            pendingRedemptionsValue: wallet.pendingRedemptionsValue,
            createdAt: wallet.createdAt,
            movingFundsRequestedAt: wallet.movingFundsRequestedAt,
            closingStartedAt: wallet.closingStartedAt,
            pendingMovedFundsSweepRequestsCount: wallet.pendingMovedFundsSweepRequestsCount,
            state: contracts_1.WalletState.parse(wallet.state),
            movingFundsTargetWalletsCommitmentHash: utils_1.Hex.from(wallet.movingFundsTargetWalletsCommitmentHash),
        };
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * Builds the UTXO hash based on the UTXO components. UTXO hash is computed as
     * `keccak256(txHash | txOutputIndex | txOutputValue)`.
     *
     * @see {Bridge#buildUtxoHash}
     */
    buildUtxoHash(utxo) {
        return utils_1.Hex.from(ethers_1.utils.solidityKeccak256(["bytes32", "uint32", "uint64"], [
            utxo.transactionHash.reverse().toPrefixedString(),
            utxo.outputIndex,
            utxo.value,
        ]));
    }
    // eslint-disable-next-line valid-jsdoc
    /**
     * @see {Bridge#getDepositRevealedEvents}
     */
    async getRedemptionRequestedEvents(options, ...filterArgs) {
        // FIXME: Filtering by indexed walletPubKeyHash field may not work
        //        until https://github.com/ethers-io/ethers.js/pull/4244 is
        //        included in the currently used version of ethers.js.
        //        Ultimately, we should upgrade ethers.js to include that fix.
        //        Short-term, we can workaround the problem as presented in:
        //        https://github.com/threshold-network/token-dashboard/blob/main/src/threshold-ts/tbtc/index.ts#L1041C1-L1093C1
        const events = await this.getEvents("RedemptionRequested", options, ...filterArgs);
        return events.map((event) => {
            const prefixedRedeemerOutputScript = utils_1.Hex.from(event.args.redeemerOutputScript);
            const redeemerOutputScript = prefixedRedeemerOutputScript
                .toString()
                .slice(bitcoin_1.BitcoinCompactSizeUint.read(prefixedRedeemerOutputScript).byteLength *
                2);
            return {
                blockNumber: ethers_1.BigNumber.from(event.blockNumber).toNumber(),
                blockHash: utils_1.Hex.from(event.blockHash),
                transactionHash: utils_1.Hex.from(event.transactionHash),
                walletPublicKeyHash: utils_1.Hex.from(event.args.walletPubKeyHash),
                redeemer: address_1.EthereumAddress.from(event.args.redeemer),
                redeemerOutputScript: utils_1.Hex.from(redeemerOutputScript),
                requestedAmount: ethers_1.BigNumber.from(event.args.requestedAmount),
                treasuryFee: ethers_1.BigNumber.from(event.args.treasuryFee),
                txMaxFee: ethers_1.BigNumber.from(event.args.txMaxFee),
            };
        });
    }
}
exports.EthereumBridge = EthereumBridge;
/**
 * Packs deposit parameters to match the ABI of the revealDeposit and
 * revealDepositWithExtraData functions of the Ethereum Bridge contract.
 * @param depositTx - Deposit transaction data
 * @param depositOutputIndex - Index of the deposit transaction output that
 *        funds the revealed deposit
 * @param deposit - Data of the revealed deposit
 * @param vault - Optional parameter denoting the vault the given deposit
 *        should be routed to
 * @returns Packed parameters.
 */
function packRevealDepositParameters(depositTx, depositOutputIndex, deposit, vault) {
    var _a;
    const fundingTx = {
        version: depositTx.version.toPrefixedString(),
        inputVector: depositTx.inputs.toPrefixedString(),
        outputVector: depositTx.outputs.toPrefixedString(),
        locktime: depositTx.locktime.toPrefixedString(),
    };
    const reveal = {
        fundingOutputIndex: depositOutputIndex,
        blindingFactor: deposit.blindingFactor.toPrefixedString(),
        walletPubKeyHash: deposit.walletPublicKeyHash.toPrefixedString(),
        refundPubKeyHash: deposit.refundPublicKeyHash.toPrefixedString(),
        refundLocktime: deposit.refundLocktime.toPrefixedString(),
        vault: vault ? `0x${vault.identifierHex}` : ethers_1.constants.AddressZero,
    };
    const extraData = (_a = deposit.extraData) === null || _a === void 0 ? void 0 : _a.toPrefixedString();
    return {
        fundingTx,
        reveal,
        extraData,
    };
}
exports.packRevealDepositParameters = packRevealDepositParameters;
//# sourceMappingURL=bridge.js.map